import type { DbExecutor } from "./executor";
import { formatCents } from "../domain/money";
import type { ProjectRepository } from "./repositories/projectRepository";
import type { StatusRepository } from "./repositories/statusRepository";
import type { CategoryRepository } from "./repositories/categoryRepository";
import type { AcquisitionRepository } from "./repositories/acquisitionRepository";
import type { FeatureRepository } from "./repositories/featureRepository";
import type { RoomRepository } from "./repositories/roomRepository";
import type { MileageRepository } from "./repositories/mileageRepository";
import type { PurchaseRepository } from "./repositories/purchaseRepository";
import type { AttachmentRepository } from "./repositories/attachmentRepository";
import type { DesignRepository } from "./repositories/designRepository";
import type { BaselineService } from "./baselineService";
import type { ProcurementService } from "./procurementService";
import type { ReconciliationService } from "./reconciliationService";
import type { ProjectFinancialsService } from "./projectFinancialsService";
import { ValidationError } from "./repositories/projectRepository";
import type { FloorRepository } from "./repositories/floorRepository";
import type { PlanRepository } from "./repositories/planRepository";
import type { ObjectDefinitionRepository } from "./repositories/objectDefinitionRepository";
import type { StairsRepository } from "./repositories/stairsRepository";
import { analyzePlan, buildScene } from "../geometry";
import { annotationPrimitives } from "../geometry/annotations";
import { stairStepCount } from "../domain/stairs";
import {
  ARCHITECTURAL_SCALES,
  SHEET_SIZES,
  describeSheet,
  layoutDesignSheet,
  multiFloorSheetSvg,
  planFloorSheets,
  resolveOrientation,
  ANNOTATION_SCALES,
  type FloorSheetEntry,
} from "../reports/designSheet";
import type { Cents } from "../domain/money";
import type { FinancialBucket } from "../domain/buckets";
import {
  cell,
  type ExportSheet,
  type ExportWorkbook,
  type ReportIdentity,
  type ReportModel,
} from "../reports/reportModel";
import {
  fullCloseoutReport,
  itemFinancialControlReport,
  projectFinancialSummaryReport,
  renovationBudgetReport,
  executionForecastReport,
  type ControlItemRow,
  type ControlLayout,
} from "../reports/projectReports";
import { proposedEstimateReport, quantityLabel, type EstimateLineInput } from "../reports/estimateReport";
import { baselineChangeOrderReport, baselineVsDesignReport, changeOrderDocument } from "../reports/budgetReports";
import {
  filterProcurementRows,
  procurementExportSheet,
  procurementReport,
  type ProcurementFilter,
  type ProcurementReportRow,
} from "../reports/procurementReport";
import {
  ALL_DATES,
  accountingExportSheet,
  calendarYear,
  documentIndexExportSheet,
  documentLabel,
  mileageExportSheet,
  projectMileageReport,
  sourceDocumentIndexReport,
  taxAccountingReport,
  withinPeriod,
  type AccountingPeriod,
  type AccountingTransactionRow,
  type DocumentStatus,
  type MileageReportRow,
} from "../reports/accountingReports";
import { designComparisonReport, type DesignScopeRow } from "../reports/designComparisonReport";
import type { ReportId } from "../reports/registry";
import { drawRequestReport } from "../reports/drawRequestReport";
import { financialCalendarReport } from "../reports/financialCalendarReport";
import { addDays } from "../engine/financing";

/**
 * REPORT DATA ASSEMBLY (Phase 13, spec §42A).
 *
 * Gathers what a report needs from the authoritative services and hands it
 * to the PURE builders in `src/reports/`. Two rules hold this together:
 *
 *   1. no report performs financial arithmetic — every figure comes from the
 *      Phase 12 engine, the Phase 9C reconciliation, the assembly/BOM engine
 *      or a frozen Phase 11 record;
 *   2. the UI never queries — it asks for a report and renders the model.
 */

export interface GeneratedReport {
  model: ReportModel;
  /** Present when the report has a tabular export. */
  workbook: ExportWorkbook | null;
}

export interface SavedReport {
  attachmentId: string;
  reportId: string;
  label: string;
  filename: string;
  sizeBytes: number;
  mimeType: string;
  createdAt: string;
  storedRelativePath: string;
}

export interface ReportOptions {
  drawRequestId?: string;
  period?: string;
  periodFrom?: string;
  periodTo?: string;
  detail?: string;
  layout?: string;
  dimension?: string;
  estimateBasis?: string;
  procurementFilter?: string;
  procurementGroup?: string;
  changeOrderId?: string;
  designA?: string;
  designB?: string;
  /** Design sheet (spec §60M): which plan, which floor, on what paper. */
  designId?: string;
  floorId?: string;
  sheetSize?: string;
  sheetScale?: string;
  sheetLayout?: string;
  sheetOrientation?: string;
  annotationSize?: string;
}

export class ReportService {
  constructor(
    private readonly db: DbExecutor,
    private readonly projects: ProjectRepository,
    private readonly statuses: StatusRepository,
    private readonly categories: CategoryRepository,
    private readonly acquisition: AcquisitionRepository,
    private readonly features: FeatureRepository,
    private readonly rooms: RoomRepository,
    private readonly mileage: MileageRepository,
    private readonly purchases: PurchaseRepository,
    private readonly attachments: AttachmentRepository,
    private readonly designs: DesignRepository,
    private readonly baselines: BaselineService,
    private readonly procurement: ProcurementService,
    private readonly reconciliation: ReconciliationService,
    private readonly financials: ProjectFinancialsService,
    // Spec §60M: a printable floor plan needs the plan itself.
    private readonly floors: FloorRepository,
    private readonly plans: PlanRepository,
    private readonly objectDefs: ObjectDefinitionRepository,
    private readonly stairs: StairsRepository,
    /* Spec §83: the drawing annotations that print with the plan. */
    private readonly annotations: import("./repositories/annotationRepository").AnnotationRepository,
    // Kept for wiring compatibility; the design sheet no longer draws
    // callouts (spec §74B) so nothing here reads the bid model any more.
    protected readonly bids?: import("./bidService").BidService,
    /** Spec §74AA: the derived category budget for the budget report. */
    private readonly categoryBudget?: import("./categoryBudgetService").CategoryBudgetService,
    /** Spec §75T: the chronological execution + money forecast. */
    private readonly executionForecast?: import("./executionForecastService").ExecutionForecastService,
    /** Spec §77L: $/sq ft benchmarks on the financial summary. */
    private readonly calibration?: import("./calibrationService").CalibrationService,
    /**
     * Spec §91-31: how much of the renovation has been closed out. An
     * OWNER-facing fact only — no contractor document reads this, and no
     * calibration figure, historical sample or exclusion control ever
     * reaches a printed page.
     */
    private readonly completion?: import("./completionService").CompletionService,
    private readonly capitalPlan?: import("./capitalPlanService").CapitalPlanService,
    private readonly drawRequests?: import("./drawRequestService").DrawRequestService,
  ) {}

  /* ------------------------------------------------------------------ */
  /* Identity                                                            */
  /* ------------------------------------------------------------------ */

  async identity(projectId: string): Promise<ReportIdentity> {
    const project = await this.projects.getProject(projectId);
    if (!project) throw new ValidationError(["That project no longer exists."]);
    const statuses = await this.statuses.listStatuses();
    const address = [project.streetAddress, [project.city, project.state].filter(Boolean).join(", "), project.zip]
      .filter((part) => part && part.trim() !== "")
      .join(" · ");
    return {
      jobNumber: project.jobNumber,
      projectName: project.name,
      address,
      projectType: project.projectType === "new_construction" ? "new_construction" : "renovation",
      statusLabel: statuses.find((s) => s.id === project.statusId)?.displayName ?? project.statusId,
    };
  }

  /* ------------------------------------------------------------------ */
  /* The one entry point                                                 */
  /* ------------------------------------------------------------------ */

  async generate(projectId: string, reportId: ReportId, options: ReportOptions = {}): Promise<GeneratedReport> {
    const identity = await this.identity(projectId);
    switch (reportId) {
      case "project-financial-summary": {
        const dashboard = await this.financials.dashboard(projectId);
        // $/sq ft benchmarks (spec §77L) — best-effort: the report stands
        // without them when the calibration service is absent.
        let sqft: Awaited<ReturnType<NonNullable<typeof this.calibration>["metrics"]>> | undefined;
        if (this.calibration) {
          try {
            sqft = await this.calibration.metrics(projectId);
          } catch {
            sqft = undefined;
          }
        }
        let closeout: { completedAt: string | null; completeCount: number; itemCount: number; learnedCount: number } | undefined;
        if (this.completion) {
          try {
            const [review, state] = await Promise.all([
              this.completion.review(projectId),
              this.completion.state(projectId),
            ]);
            closeout = {
              completedAt: state.renovationCompletedAt,
              completeCount: review.summary.completeCount,
              itemCount: review.summary.featureCount,
              learnedCount: review.summary.willTeachCount,
            };
          } catch {
            closeout = undefined;
          }
        }
        return { model: projectFinancialSummaryReport(dashboard, identity, sqft, closeout), workbook: null };
      }
      case "renovation-budget": {
        if (!this.categoryBudget) throw new Error("The renovation budget is not available in this build.");
        const [dashboard, budget] = await Promise.all([
          this.financials.dashboard(projectId),
          this.categoryBudget.model(projectId),
        ]);
        return { model: renovationBudgetReport(dashboard, budget, identity), workbook: null };
      }
      case "construction-draw-request": {
        if(!this.drawRequests)throw new Error("Draw requests are not available in this build.");
        if(!options.drawRequestId)throw new ValidationError(["Choose a saved draw request to preview."]);
        return {model:drawRequestReport(await this.drawRequests.get(projectId,options.drawRequestId),identity,new Date().toISOString()),workbook:null};
      }
      case "financial-calendar": {
        if(!this.capitalPlan)throw new Error("The financial calendar is not available in this build.");
        const start=new Date().toISOString().slice(0,10);
        return {model:financialCalendarReport(await this.capitalPlan.calendar(projectId,start,addDays(start,90),start),identity,new Date().toISOString()),workbook:null};
      }
      case "execution-forecast": {
        if (!this.executionForecast) throw new Error("The execution forecast is not available in this build.");
        const [dashboard, result] = await Promise.all([
          this.financials.dashboard(projectId),
          this.executionForecast.forecast(projectId),
        ]);
        return { model: executionForecastReport(dashboard, result, identity), workbook: null };
      }
      case "financial-control":
        return this.financialControl(projectId, identity, options);
      case "closeout":
        return this.closeout(projectId, identity);
      case "proposed-estimate":
        return this.estimate(projectId, identity, options);
      case "baseline-change-orders":
        return this.budgetHistory(projectId, identity);
      case "change-order":
        return this.changeOrder(projectId, identity, options);
      case "procurement":
        return this.procurementReport(projectId, identity, options);
      case "design-sheet":
        return this.designSheet(projectId, identity, options);
      case "design-comparison":
        return this.designComparison(projectId, identity, options);
      case "baseline-vs-design":
        return this.baselineVsDesign(projectId, identity, options);
      case "tax-accounting":
        return this.taxAccounting(projectId, identity, options);
      case "mileage":
        return this.mileageReport(projectId, identity, options);
      case "source-documents":
        return this.sourceDocuments(projectId, identity, options);
      default:
        throw new ValidationError(["That report does not exist."]);
    }
  }

  /* ------------------------------------------------------------------ */
  /* Financial control                                                   */
  /* ------------------------------------------------------------------ */

  private async financialControl(
    projectId: string,
    identity: ReportIdentity,
    options: ReportOptions,
  ): Promise<GeneratedReport> {
    const dimension = (options.dimension ?? "feature") as "room" | "category" | "feature" | "basis";
    const layout = (options.layout ?? "default") as ControlLayout;
    const [dashboard, rollups, budget] = await Promise.all([
      this.financials.dashboard(projectId),
      this.financials.rollups(projectId, dimension),
      this.baselines.budget(projectId),
    ]);

    // Baseline and authorized figures per item come from the FROZEN records:
    // the baseline lines, plus the change-order lines already approved.
    //
    // The keys MUST agree with the projection's own roll-up keys, or an item
    // that was in the baseline reads as unauthorized new scope (it did — a
    // manual line with no feature keyed on an empty string, so the whole
    // authorized column understated the budget; found in acceptance).
    const baselineByKey = new Map<string, Cents>();
    const authorizedByKey = new Map<string, Cents>();
    const keyFor = (row: {
      featureId: string | null;
      featureName: string;
      roomName: string;
      description: string;
      categoryId: string | null;
      costType: string | null;
    }) => {
      switch (dimension) {
        case "room":
          return roomKey(row.roomName);
        case "category":
          return row.categoryId ?? "uncategorized";
        case "basis":
          return row.costType ?? "unattributed";
        default:
          // Mirrors rollupKey(): featureId, else the name the projection uses
          // for a feature-less line, which is its description.
          return row.featureId ?? (row.featureName || row.description);
      }
    };
    for (const line of budget.baseline?.lines ?? []) {
      const key = keyFor({ ...line, description: line.description });
      baselineByKey.set(key, (baselineByKey.get(key) ?? 0) + line.amountCents);
      authorizedByKey.set(key, (authorizedByKey.get(key) ?? 0) + line.amountCents);
    }
    for (const order of budget.changeOrders.filter((c) => c.status === "approved")) {
      for (const line of order.lines) {
        const key = keyFor({
          featureId: line.featureId,
          featureName: line.featureName,
          roomName: line.roomName,
          description: line.description,
          categoryId: line.categoryId,
          costType: line.costType,
        });
        authorizedByKey.set(key, (authorizedByKey.get(key) ?? 0) + line.deltaCents);
      }
    }

    const items: ControlItemRow[] = rollups.map((row) => {
      const rowKey = dimension === "room" ? roomKey(row.label) : row.key;
      const baselineCents = baselineByKey.has(rowKey) ? baselineByKey.get(rowKey)! : null;
      const authorizedCents = authorizedByKey.has(rowKey) ? authorizedByKey.get(rowKey)! : null;
      return {
        key: rowKey,
        label: row.label,
        featureId: dimension === "feature" ? row.key : null,
        baselineCents,
        authorizedCents,
        proposedCents: row.proposedCents,
        actualCents: row.actualCents,
        projectedCents: row.projectedCents,
        varianceCents: row.projectedCents - (authorizedCents ?? row.proposedCents),
        incomplete: row.incomplete,
        statusLabel: row.incomplete
          ? row.actualCents === 0
            ? "Unreconciled"
            : "Partial"
          : "Complete",
        note:
          baselineCents === null && budget.baseline !== null
            ? "Added after the baseline — authorized only through a change order."
            : undefined,
      };
    });

    const model = itemFinancialControlReport({
      dashboard,
      items,
      dimension,
      layout,
      identity,
      contingencyCents: dashboard.rehab.remainingContingencyCents,
    });
    const sheet: ExportSheet = {
      name: "Financial control",
      columns: [
        { key: "item", label: "Item" },
        { key: "baseline", label: "Baseline" },
        { key: "authorized", label: "Authorized" },
        { key: "current", label: "Current proposed" },
        { key: "actual", label: "Actual recorded" },
        { key: "projected", label: "Projected final" },
        { key: "variance", label: "Variance" },
        { key: "status", label: "Status" },
      ],
      rows: items.map((item) => [
        cell.text(item.label),
        item.baselineCents === null ? cell.empty() : cell.money(item.baselineCents),
        item.authorizedCents === null ? cell.empty() : cell.money(item.authorizedCents),
        cell.money(item.proposedCents),
        cell.money(item.actualCents),
        cell.money(item.projectedCents),
        item.incomplete ? cell.empty() : cell.money(item.varianceCents),
        cell.text(item.statusLabel),
      ]),
    };
    return { model, workbook: { fileBaseName: exportName(identity, "financial-control"), sheets: [sheet] } };
  }

  /* ------------------------------------------------------------------ */
  /* Closeout                                                            */
  /* ------------------------------------------------------------------ */

  private async closeout(projectId: string, identity: ReportIdentity): Promise<GeneratedReport> {
    const [dashboard, acquisitionLines, procurementRows, transactions] = await Promise.all([
      this.financials.dashboard(projectId),
      this.acquisition.listByProject(projectId),
      this.procurementRows(projectId),
      this.transactionRows(projectId),
    ]);
    const attached = transactions.filter((row) => row.documentStatus === "attached").length;
    const missingRows = transactions.filter((row) => row.documentStatus === "missing");
    const model = fullCloseoutReport(dashboard, {
      identity,
      variances: dashboard.projection.lines,
      overbuy: procurementRows
        .filter((row) => !row.quantityReviewRequired && !row.remainingQuantityUnknown && row.excessUnitsX100 > 0)
        .map((row) => ({
          productName: row.productName,
          excessUnits: row.excessUnitsX100 / 100,
          unit: row.purchaseUnit ?? row.unit,
          spentCents: row.spentCents,
        })),
      documents: {
        attached,
        missing: missingRows.length,
        missingCents: missingRows.reduce((sum, row) => sum + row.totalCents, 0),
      },
      acquisitionLines: acquisitionLines
        .filter((line) => line.archivedAt === null)
        .map((line) => ({
          description: line.description,
          amountCents: line.amountCents,
          credited: line.creditedTowardPurchase,
        })),
    });
    return { model, workbook: null };
  }

  /* ------------------------------------------------------------------ */
  /* Proposed estimate                                                   */
  /* ------------------------------------------------------------------ */

  private async estimate(projectId: string, identity: ReportIdentity, options: ReportOptions): Promise<GeneratedReport> {
    const basis = options.estimateBasis === "baseline" ? "baseline" : "working";
    const detail = options.detail === "detailed" ? "detailed" : "summary";
    const [project, budget, designs] = await Promise.all([
      this.projects.getProject(projectId),
      this.baselines.budget(projectId),
      this.designs.listByProject(projectId),
    ]);
    if (!project) throw new ValidationError(["That project no longer exists."]);

    const productUrls = await this.productUrls();
    let lines: EstimateLineInput[];
    let designName: string;
    let designRole: string;
    let contingencyCents: Cents;

    if (basis === "baseline") {
      const baseline = budget.baseline;
      if (!baseline) {
        throw new ValidationError(["This project has no approved baseline yet, so there is no baseline estimate to print."]);
      }
      lines = baseline.lines.map((line) => ({
        featureId: line.featureId,
        featureName: line.featureName || line.description,
        roomName: line.roomName,
        description: line.description,
        amountCents: line.amountCents,
        costType: line.costType,
        quantityX100: line.quantityX100,
        quantityUnit: line.quantityUnit,
        unitCostCents: line.unitCostCents,
        productName: line.productName,
        productUrl: line.productId ? (productUrls.get(line.productId) ?? null) : null,
      }));
      designName = baseline.designName || "No design";
      designRole = "approved design at baseline";
      contingencyCents = baseline.contingencyCents;
    } else {
      const activeDesignId = budget.activeDesignId;
      const current = await this.baselines.currentLines(projectId, activeDesignId);
      lines = current.map((line) => ({
        featureId: line.featureId,
        featureName: line.featureName || line.description,
        roomName: line.roomName,
        description: line.description,
        amountCents: line.amountCents,
        costType: line.costType,
        quantityX100: line.quantityX100,
        quantityUnit: line.quantityUnit,
        unitCostCents: line.unitCostCents,
        productName: line.productName,
        productUrl: line.productId ? (productUrls.get(line.productId) ?? null) : null,
      }));
      const active = designs.find((design) => design.id === activeDesignId);
      designName = active?.name ?? budget.activeDesignName ?? "No design";
      designRole =
        activeDesignId && activeDesignId === budget.approvedDesignId
          ? "Active Design · also the approved design"
          : activeDesignId
            ? "Active Design — not the approved design"
            : "project scope";
      contingencyCents = Number(project.contingencyCents ?? 0);
    }

    const model = proposedEstimateReport({
      identity,
      basis,
      detail,
      designName,
      designRole,
      baselineName: budget.baseline?.name,
      baselineApprovedAt: budget.baseline?.approvedAt,
      contingencyCents,
      estimatingTaxPctX100: project.estimatingTaxPctX100 ?? null,
      lines,
      authorizedCents: basis === "working" ? (budget.baseline ? budget.authorizedBudgetCents : null) : undefined,
      generatedAt: new Date().toISOString(),
    });

    const sheet: ExportSheet = {
      name: "Estimate",
      columns: [
        { key: "room", label: "Room" },
        { key: "feature", label: "Feature" },
        { key: "description", label: "Line" },
        { key: "basis", label: "Cost basis" },
        { key: "quantity", label: "Quantity" },
        { key: "unitCost", label: "Unit cost" },
        { key: "amount", label: "Amount" },
        { key: "product", label: "Product" },
      ],
      rows: lines.map((line) => [
        cell.text(line.roomName || "Project-wide"),
        cell.text(line.featureName),
        cell.text(line.description),
        cell.text(line.costType ?? ""),
        cell.text(quantityLabel(line.quantityX100, line.quantityUnit) ?? ""),
        line.unitCostCents === null ? cell.empty() : cell.money(line.unitCostCents),
        cell.money(line.amountCents),
        cell.text(line.productName),
      ]),
    };
    return { model, workbook: { fileBaseName: exportName(identity, `estimate-${basis}`), sheets: [sheet] } };
  }

  /* ------------------------------------------------------------------ */
  /* Baseline / change orders                                            */
  /* ------------------------------------------------------------------ */

  private async budgetHistory(projectId: string, identity: ReportIdentity): Promise<GeneratedReport> {
    const [baselines, budget, dashboard] = await Promise.all([
      this.baselines.listBaselines(projectId),
      this.baselines.budget(projectId),
      this.financials.dashboard(projectId),
    ]);
    const model = baselineChangeOrderReport({
      identity,
      baselines,
      changeOrders: budget.changeOrders,
      authorizedCents: dashboard.rehab.hasBaseline ? dashboard.rehab.authorizedCents : 0,
      remainingContingencyCents: dashboard.rehab.remainingContingencyCents,
      currentWorkingCents: budget.currentWorkingCents,
      generatedAt: new Date().toISOString(),
    });
    const sheet: ExportSheet = {
      name: "Change orders",
      columns: [
        { key: "number", label: "Number" },
        { key: "title", label: "Title" },
        { key: "status", label: "Status" },
        { key: "funding", label: "Funding source" },
        { key: "approved", label: "Approved" },
        { key: "item", label: "Item" },
        { key: "room", label: "Room" },
        { key: "previous", label: "Previous amount" },
        { key: "new", label: "New amount" },
        { key: "delta", label: "Delta" },
      ],
      rows: budget.changeOrders.flatMap((order) =>
        order.lines.map((line) => [
          cell.text(order.number),
          cell.text(order.title),
          cell.text(order.status),
          cell.text(order.fundingSource),
          cell.date(order.approvedAt ? order.approvedAt.slice(0, 10) : ""),
          cell.text(line.description),
          cell.text(line.roomName),
          cell.money(line.baselineAmountCents),
          cell.money(line.currentAmountCents),
          cell.money(line.deltaCents),
        ]),
      ),
    };
    return { model, workbook: { fileBaseName: exportName(identity, "change-orders"), sheets: [sheet] } };
  }

  private async changeOrder(projectId: string, identity: ReportIdentity, options: ReportOptions): Promise<GeneratedReport> {
    if (!options.changeOrderId) throw new ValidationError(["Choose a change order to print."]);
    const [order, budget, dashboard] = await Promise.all([
      this.baselines.getChangeOrder(options.changeOrderId),
      this.baselines.budget(projectId),
      this.financials.dashboard(projectId),
    ]);
    if (!order || order.projectId !== projectId) throw new ValidationError(["That change order no longer exists."]);
    const authorizedAfter = dashboard.rehab.authorizedCents;
    const contributes = order.status === "approved" && order.fundingSource !== "contingency";
    const model = changeOrderDocument({
      identity,
      order,
      baselineName: budget.baseline?.name ?? "—",
      authorizedBeforeCents: contributes ? authorizedAfter - (order.netDeltaCents - order.contingencyCents) : authorizedAfter,
      authorizedAfterCents: contributes ? authorizedAfter : authorizedAfter,
      generatedAt: new Date().toISOString(),
    });
    return { model, workbook: null };
  }

  private async baselineVsDesign(
    projectId: string,
    identity: ReportIdentity,
    options: ReportOptions,
  ): Promise<GeneratedReport> {
    if (!options.designB) throw new ValidationError(["Choose a candidate design to compare."]);
    const [budget, dashboard, project, designs] = await Promise.all([
      this.baselines.budget(projectId),
      this.financials.dashboard(projectId),
      this.projects.getProject(projectId),
      this.designs.listByProject(projectId),
    ]);
    const candidate = designs.find((design) => design.id === options.designB);
    if (!candidate) throw new ValidationError(["That design no longer exists."]);
    const rows = await this.designScopeRows(projectId, candidate.id);
    const model = baselineVsDesignReport({
      identity,
      authorizedCents: dashboard.rehab.hasBaseline ? dashboard.rehab.authorizedCents : 0,
      approvedDesignName: budget.approvedDesignName || "Authorized budget",
      candidateDesignName: candidate.name,
      candidateCents: rows.reduce((sum, row) => sum + row.amountCents, 0),
      rows: rows.map((row) => ({
        label: `${row.roomName || "Project-wide"} · ${row.featureName}`,
        amountCents: row.amountCents,
      })),
      contingencyCents: budget.baseline?.contingencyCents ?? Number(project?.contingencyCents ?? 0),
      generatedAt: new Date().toISOString(),
    });
    return { model, workbook: null };
  }

  /* ------------------------------------------------------------------ */
  /* Designs                                                             */
  /* ------------------------------------------------------------------ */

  /**
   * A PRINTABLE FLOOR PLAN (spec §60M).
   *
   * The same geometry the editor draws, emitted as a vector sheet sized in
   * real inches — never a screenshot of the canvas, and never labelled with
   * a scale it does not have. This method assembles; it computes nothing.
   */
  private async designSheet(
    projectId: string,
    identity: ReportIdentity,
    options: ReportOptions,
  ): Promise<GeneratedReport> {
    const designs = await this.designs.listByProject(projectId);
    const design = designs.find((d) => d.id === options.designId) ?? designs[0];
    if (!design) throw new ValidationError(["This project has no design to print."]);
    const allFloors = (await this.floors.listByProject(projectId)).filter((f) => f.archivedAt === null);
    // EVERY floor of the design prints (spec §69-1). Asking for one specific
    // floor still works; the default is the whole building.
    const wantedFloors =
      options.floorId && options.floorId !== "all"
        ? allFloors.filter((f) => f.id === options.floorId)
        : allFloors;
    if (wantedFloors.length === 0) throw new ValidationError(["This project has no floor to print."]);
    const floor = wantedFloors[0];

    const [rooms, definitions] = await Promise.all([
      this.rooms.listByProject(projectId),
      this.objectDefs.listAll(),
    ]);
    const roomNames = new Map(rooms.map((room) => [room.id, room.name]));
    const objectMeta = new Map(definitions.map((d) => [d.id, { symbolKey: d.symbolKey, category: d.category }]));

    const sceneFor = async (floorId: string) => {
      const floorModel = await this.plans.loadFloor(design.id, floorId);
      const floorAnalysis = analyzePlan(floorModel);
      const stairRows = await this.stairs.listByDesignFloor(design.id, floorId);
      /* Spec §83: the drawing's own annotations print with it — the same
       * ScenePrimitives the canvas draws, so paper and screen agree. */
      const notes = await this.annotations.listForFloor(design.id, floorId);
      const scene = buildScene(floorModel, floorAnalysis, {
          selection: {},
          hover: {},
          scale: 0.02,
          showLabels: true,
          showBoundaries: true,
          roomNames,
          objectMeta,
          stairs: new Map(
            [...stairRows.entries()].map(([objectId, stair]) => [
              objectId,
              { stepCount: stairStepCount(stair), upDirection: stair.upDirection },
            ]),
          ),
      });
      return {
        model: floorModel,
        scene: {
          ...scene,
          static: [...scene.static, ...annotationPrimitives(floorModel, notes)],
        },
        stairCount: stairRows.size,
        annotationCount: notes.length,
      };
    };

    const perFloor = new Map<string, Awaited<ReturnType<typeof sceneFor>>>();
    for (const f of wantedFloors) perFloor.set(f.id, await sceneFor(f.id));
    const first = perFloor.get(floor.id)!;
    const model = first.model;
    const analysis = analyzePlan(model);
    const scene = first.scene;
    const stairRows = { size: first.stairCount } as { size: number };

    const size = SHEET_SIZES.find((s) => s.id === options.sheetSize) ?? SHEET_SIZES[0];
    const scale =
      options.sheetScale === "fit"
        ? ("fit" as const)
        : ARCHITECTURAL_SCALES.find((s) => s.id === options.sheetScale) ?? ARCHITECTURAL_SCALES[1];
    // ANNOTATION SIZE (spec §70C): a paper-space presentation preference —
    // it never touches geometry, measurements or the architectural scale.
    const annotationScale = ANNOTATION_SCALES[options.annotationSize ?? "standard"] ?? ANNOTATION_SCALES.standard;
    const requestedOrientation =
      options.sheetOrientation === "portrait" || options.sheetOrientation === "landscape"
        ? options.sheetOrientation
        : ("auto" as const);
    // Placeholder orientation; the real choice is made once the floor
    // entries exist (Auto weighs every floor being printed, spec §70F).
    let sheetOptions = { size, scale, orientation: "landscape" as const as "landscape" | "portrait", annotationScale };
    let layout = layoutDesignSheet(scene, sheetOptions);
    const project = await this.projects.getProject(projectId);
    const generatedAt = new Date().toISOString();
    const info = {
      projectName: identity.projectName,
      address: [project?.streetAddress, project?.city, project?.state, project?.zip].filter(Boolean).join(", "),
      jobNumber: identity.jobNumber,
      designName: design.kind === "existing" ? "Existing" : design.name,
      designKind: design.kind,
      floorName: floor.name,
      generatedAt,
      sheetTitle: "FLOOR PLAN",
      sheetNumber: "A-1",
    };
    const entries: FloorSheetEntry[] = wantedFloors.map((f) => ({
      floorId: f.id,
      floorName: f.name,
      scene: perFloor.get(f.id)!.scene,
    }));
    const layoutModeEarly = (options.sheetLayout as "auto" | "per-floor" | "fit" | undefined) ?? "auto";
    const orientation = resolveOrientation(
      entries,
      { size, scale, annotationScale },
      requestedOrientation,
      scale === "fit" ? "fit" : layoutModeEarly,
    );
    sheetOptions = { ...sheetOptions, orientation };
    layout = layoutDesignSheet(scene, sheetOptions);
    // ONE declared scale for the whole set (spec §69-1). Each floor alone
    // would step down its own ladder — a 30ft floor at 3/16" beside a 24ft
    // floor at 1/4" — and two pages of one drawing must not disagree about
    // what an inch means. The scale that fits the LARGEST floor fits all.
    let effectiveOptions = sheetOptions;
    if (sheetOptions.scale !== "fit") {
      let smallest = sheetOptions.scale;
      for (const entry of entries) {
        const floorLayout = layoutDesignSheet(entry.scene, sheetOptions);
        if (floorLayout.scale && floorLayout.scale.paperInchesPerFoot < smallest.paperInchesPerFoot) {
          smallest = floorLayout.scale;
        }
      }
      effectiveOptions = { ...sheetOptions, scale: smallest };
      // The page must describe the scale it actually used.
      layout = layoutDesignSheet(scene, effectiveOptions);
    }
    const layoutMode = (options.sheetLayout as "auto" | "per-floor" | "fit" | undefined) ?? "auto";
    const sheets = planFloorSheets(entries, effectiveOptions, effectiveOptions.scale === "fit" ? "fit" : layoutMode);
    // NO Q CALLOUTS, NO LEADER LINES (spec §74B — permanent). The drawing
    // is a clean visual reference; the written bid scope identifies work by
    // name and location. Stored bid_callouts rows from earlier builds are
    // simply unused — never a rendering input again.
    const sheetSvgs = sheets.map((sheet) =>
      multiFloorSheetSvg(
        sheet.floors,
        { ...info, floorName: sheet.floors.map((f) => f.floorName).join(" · "), sheetNumber: sheet.sheetNumber },
        effectiveOptions,
      ),
    );
    const svg = sheetSvgs[0];

    // What the drawing contains, counted from the same model it drew.
    const openings = [...model.openings.values()].filter((o) => o.archivedAt === null);
    const windows = openings.filter((o) => o.kind === "window");
    const byConfiguration = new Map<string, number>();
    for (const window of windows) {
      byConfiguration.set(window.windowConfig, (byConfiguration.get(window.windowConfig) ?? 0) + 1);
    }
    const sheetModel: ReportModel = {
      id: "design-sheet",
      title: `Floor plan — ${info.designName}`,
      subtitle: `${floor.name} · ${describeSheet(layout, size)}`,
      identity,
      generatedAt,
      drawing: {
        svg,
        caption: layout.scaleReduced ? "The requested scale did not fit this sheet." : "",
        widthIn: layout.paper.widthIn,
        heightIn: layout.paper.heightIn,
      },
      drawings: sheetSvgs.slice(1).map((extra, index) => ({
        svg: extra,
        caption: sheets[index + 1].floors.map((f) => f.floorName).join(" · "),
      })),
      warnings: (() => {
        const all: string[] = [];
        // A sheet with a title block and no building is the failure mode
        // this report once shipped with (spec §69-0). Say it out loud.
        const empty = wantedFloors.filter((f) => {
          const m = perFloor.get(f.id)!.model;
          return [...m.walls.values()].every((w) => w.archivedAt !== null);
        });
        if (empty.length > 0) {
          all.push(`${empty.map((f) => f.name).join(", ")} ${empty.length === 1 ? "has" : "have"} no geometry to draw yet.`);
        }
        // AN EXPLICIT SCALE IS AUTHORITATIVE (spec §70G): when it does not
        // fit, the sheet says so plainly and suggests real remedies —
        // never a quiet shrink.
        if (scale !== "fit" && wantedFloors.some((f) => layoutDesignSheet(perFloor.get(f.id)!.scene, sheetOptions).scaleReduced)) {
          const other = orientation === "landscape" ? "portrait" : "landscape";
          const fitsOther = wantedFloors.every(
            (f) => !layoutDesignSheet(perFloor.get(f.id)!.scene, { ...sheetOptions, orientation: other }).scaleReduced,
          );
          const paperLabel = orientation === "landscape" ? `${size.heightIn} × ${size.widthIn}` : `${size.widthIn} × ${size.heightIn}`;
          const remedies: string[] = [];
          if (fitsOther) remedies.push(`use ${other[0].toUpperCase()}${other.slice(1)}`);
          const biggerSheet = SHEET_SIZES.find(
            (candidate) =>
              candidate.widthIn > size.widthIn &&
              wantedFloors.every(
                (f) => !layoutDesignSheet(perFloor.get(f.id)!.scene, { ...sheetOptions, size: candidate }).scaleReduced,
              ),
          );
          if (biggerSheet) remedies.push(`choose ${biggerSheet.label}`);
          const achieved = layout.scale;
          if (achieved) remedies.push(`choose ${achieved.label}`);
          remedies.push("use Fit to sheet");
          all.push(
            `This floor does not fit on ${paperLabel} ${orientation[0].toUpperCase()}${orientation.slice(1)} at ${scale.label}. ` +
              `The sheet below is drawn at ${achieved ? achieved.label : "a fitted size"} instead — or ${remedies.join(", or ")}.`,
          );
        }
        return all.length > 0 ? all : undefined;
      })(),
      sections: [
        {
          title: "What is on this sheet",
          columns: ["Count"],
          rows: [
            { label: "Rooms", values: [{ kind: "count", value: analysis.regions.filter((r) => r.roomIds.length > 0).length }] },
            { label: "Doors", values: [{ kind: "count", value: openings.filter((o) => o.kind === "door").length }] },
            ...[...byConfiguration.entries()].map(([configuration, count]) => ({
              label: `Windows — ${configuration}`,
              values: [{ kind: "count" as const, value: count }],
            })),
            { label: "Staircases", values: [{ kind: "count", value: stairRows.size }] },
          ],
        },
      ],
      disclosures: [
        layout.scale
          ? `Drawn at ${layout.scale.label}. Print at 100% (no page scaling) for the scale to hold.`
          : "This drawing was fitted to the sheet and is NOT to scale — do not measure from it.",
        "Dimensions shown are from the plan model, not a site survey.",
      ],
    };
    return { model: sheetModel, workbook: null };
  }

  private async designComparison(
    projectId: string,
    identity: ReportIdentity,
    options: ReportOptions,
  ): Promise<GeneratedReport> {
    const designs = await this.designs.listByProject(projectId);
    const a = designs.find((design) => design.id === options.designA);
    const b = designs.find((design) => design.id === options.designB);
    if (!a || !b) throw new ValidationError(["Choose two designs to compare."]);
    const [rowsA, rowsB, project, budget] = await Promise.all([
      this.designScopeRows(projectId, a.id),
      this.designScopeRows(projectId, b.id),
      this.projects.getProject(projectId),
      this.baselines.budget(projectId),
    ]);
    const contingency = Number(project?.contingencyCents ?? 0);
    const roleOf = (design: { id: string; kind: string }) =>
      [
        design.kind === "existing" ? "Existing — the house as it is" : null,
        design.id === budget.activeDesignId ? "Active" : null,
        design.id === budget.approvedDesignId ? "Approved" : null,
      ]
        .filter(Boolean)
        .join(" · ") || "proposal";
    const model = designComparisonReport({
      identity,
      designA: { id: a.id, name: a.name, role: roleOf(a), rows: rowsA, contingencyCents: contingency },
      designB: { id: b.id, name: b.name, role: roleOf(b), rows: rowsB, contingencyCents: contingency },
      generatedAt: new Date().toISOString(),
    });
    const sheet: ExportSheet = {
      name: "Design comparison",
      columns: [
        { key: "room", label: "Room" },
        { key: "feature", label: "Feature" },
        { key: "a", label: a.name },
        { key: "b", label: b.name },
        { key: "difference", label: "Difference" },
      ],
      rows: comparisonRows(rowsA, rowsB),
    };
    return { model, workbook: { fileBaseName: exportName(identity, "design-comparison"), sheets: [sheet] } };
  }

  /** Room/feature scope totals for ONE design — never switches the active one. */
  private async designScopeRows(projectId: string, designId: string): Promise<DesignScopeRow[]> {
    const lines = await this.baselines.currentLines(projectId, designId);
    const totals = new Map<string, DesignScopeRow>();
    for (const line of lines) {
      const key = `${line.roomName}|${line.featureName || line.description}`;
      const existing = totals.get(key);
      if (existing) {
        existing.amountCents += line.amountCents;
      } else {
        totals.set(key, {
          roomName: line.roomName,
          featureName: line.featureName || line.description,
          amountCents: line.amountCents,
        });
      }
    }
    return [...totals.values()];
  }

  /* ------------------------------------------------------------------ */
  /* Procurement                                                         */
  /* ------------------------------------------------------------------ */

  private async procurementReport(
    projectId: string,
    identity: ReportIdentity,
    options: ReportOptions,
  ): Promise<GeneratedReport> {
    const filter = (options.procurementFilter ?? "all") as ProcurementFilter;
    const groupBy = (options.procurementGroup ?? "supplier") as "supplier" | "room" | "status";
    const rows = await this.procurementRows(projectId);
    const model = procurementReport({ identity, rows, filter, groupBy, generatedAt: new Date().toISOString() });
    const sheet = procurementExportSheet(filterProcurementRows(rows, filter));
    return { model, workbook: { fileBaseName: exportName(identity, `procurement-${filter}`), sheets: [sheet] } };
  }

  private async procurementRows(projectId: string): Promise<ProcurementReportRow[]> {
    const [rows, features] = await Promise.all([
      this.procurement.forProject(projectId),
      this.features.listByProject(projectId),
    ]);
    const featureName = new Map(features.map((feature) => [feature.id, feature.name]));
    const urls = await this.productUrls();
    return rows.map((row) => ({
      productName: row.productName,
      supplierName: row.supplierName,
      productUrl: row.productId ? (urls.get(row.productId) ?? null) : null,
      productId: row.productId,
      scopeLabels: row.scopeLabels,
      featureNames: row.featureIds.map((id) => featureName.get(id) ?? "").filter(Boolean),
      unit: row.unit,
      purchaseUnit: row.purchaseUnit,
      requiredX100: row.requiredX100,
      requiredUnits: row.fulfillment.requiredUnits,
      purchasedUnitsX100: row.fulfillment.purchasedUnitsX100,
      remainingUnitsX100: row.fulfillment.remainingUnitsX100,
      excessUnitsX100: row.fulfillment.excessUnitsX100,
      packageExcessX100: row.packageExcessX100,
      unitPriceCents: row.priceCentsSnapshot,
      estimatedTotalCents: row.estimatedTotalCents,
      spentCents: row.fulfillment.spentCents,
      status: row.fulfillment.status,
      statusLabel: procurementStatusLabel(row.fulfillment.status),
      disposition: row.disposition,
      needsSelection: row.needsSelection,
      quantityReviewRequired: row.quantityReviewRequired,
      remainingQuantityUnknown: row.fulfillment.remainingQuantityUnknown,
      snapshotNotes: row.snapshotGroups?.map(group => `${group.components.map(component => component.scopeLabel).join(", ")}: ${group.effectivePurchaseUnits} ${group.purchaseUnit}, coverage ${group.coveragePerUnitX100 === null ? "not confirmed" : group.coveragePerUnitX100 / 100} ${group.unit}/${group.purchaseUnit}, ${group.priceCentsSnapshot === null ? "price not confirmed" : formatCents(group.priceCentsSnapshot)}${group.priceIncludesTax ? " tax included" : " before tax"}`),
    }));
  }

  /**
   * Supplier links. The product's own URL is the primary one; the newest
   * offer URL is the fallback (a live offer carries its listing link).
   */
  private async productUrls(): Promise<Map<string, string>> {
    const rows = await this.db.select<{ product_id: string; product_url: string | null; offer_url: string | null }>(
      `SELECT p.id AS product_id, p.product_url,
              (SELECT o.offer_url FROM price_observations o
                WHERE o.product_id = p.id AND o.offer_url IS NOT NULL AND o.offer_url <> ''
                ORDER BY o.observed_at DESC, o.created_at DESC, o.rowid DESC LIMIT 1) AS offer_url
         FROM products p`,
      [],
    );
    const urls = new Map<string, string>();
    for (const row of rows) {
      const url = (row.product_url && row.product_url.trim()) || row.offer_url;
      if (url) urls.set(row.product_id, url);
    }
    return urls;
  }

  /* ------------------------------------------------------------------ */
  /* Accounting                                                          */
  /* ------------------------------------------------------------------ */

  private periodFrom(options: ReportOptions): AccountingPeriod {
    if (options.period === "year") return calendarYear(new Date().getFullYear());
    if (options.period === "range" && (options.periodFrom || options.periodTo)) {
      const from = options.periodFrom || null;
      const to = options.periodTo || null;
      return {
        from,
        to,
        label: `${from ?? "start"} to ${to ?? "today"}`,
      };
    }
    return ALL_DATES;
  }

  private async taxAccounting(
    projectId: string,
    identity: ReportIdentity,
    options: ReportOptions,
  ): Promise<GeneratedReport> {
    const period = this.periodFrom(options);
    const rows = await this.transactionRows(projectId);
    const model = taxAccountingReport({ identity, rows, period, generatedAt: new Date().toISOString() });
    const inPeriod = rows.filter((row) => withinPeriod(row.date, period));
    const mileageRows = await this.mileageRows(projectId, period);
    const workbook: ExportWorkbook = {
      fileBaseName: exportName(identity, "accounting"),
      sheets: [
        accountingExportSheet(inPeriod, {
          jobNumber: identity.jobNumber,
          projectName: identity.projectName,
          address: identity.address,
          projectType: identity.projectType === "new_construction" ? "New construction" : "Renovation",
        }),
        mileageExportSheet(mileageRows, { jobNumber: identity.jobNumber, projectName: identity.projectName }),
      ],
    };
    return { model, workbook };
  }

  private async mileageReport(
    projectId: string,
    identity: ReportIdentity,
    options: ReportOptions,
  ): Promise<GeneratedReport> {
    const period = this.periodFrom(options);
    const rows = await this.mileageRows(projectId, period);
    const model = projectMileageReport({ identity, rows, period, generatedAt: new Date().toISOString() });
    return {
      model,
      workbook: {
        fileBaseName: exportName(identity, "mileage"),
        sheets: [mileageExportSheet(rows, { jobNumber: identity.jobNumber, projectName: identity.projectName })],
      },
    };
  }

  private async mileageRows(projectId: string, period: AccountingPeriod): Promise<MileageReportRow[]> {
    const entries = await this.mileage.listByProject(projectId);
    return entries
      .filter((entry) => entry.voidedAt === null && withinPeriod(entry.date, period))
      .map((entry) => ({
        date: entry.date,
        purpose: entry.purpose,
        startLocation: entry.startLocation,
        destination: entry.destination,
        milesX10: entry.milesX10,
        rateCentsPerMile: entry.rateCentsPerMile,
        amountCents: entry.amountCents,
        bucketLabel: bucketLabel(entry.bucket),
        notes: entry.notes,
      }));
  }

  private async sourceDocuments(
    projectId: string,
    identity: ReportIdentity,
    options: ReportOptions,
  ): Promise<GeneratedReport> {
    const period = this.periodFrom(options);
    const rows = await this.transactionRows(projectId);
    const model = sourceDocumentIndexReport({ identity, rows, period, generatedAt: new Date().toISOString() });
    return {
      model,
      workbook: {
        fileBaseName: exportName(identity, "source-documents"),
        sheets: [documentIndexExportSheet(rows.filter((row) => withinPeriod(row.date, period)))],
      },
    };
  }

  /**
   * ONE ROW PER TRANSACTION OR ALLOCATION — the accountant-grade truth.
   * Purchases that span several projects contribute only their allocation.
   */
  async transactionRows(projectId: string): Promise<AccountingTransactionRow[]> {
    const [events, categories, features, rooms, mileageEntries, graphs] = await Promise.all([
      this.reconciliation.eventsForProject(projectId),
      this.categories.listAll(),
      this.features.listByProject(projectId),
      this.rooms.listByProject(projectId),
      this.mileage.listByProject(projectId),
      this.purchases.listTouchingProject(projectId),
    ]);
    const live = events.filter((event) => event.voidedAt === null);
    const provenance = await this.reconciliation.provenance(live.map((event) => event.id), projectId);
    const byEvent = new Map(provenance.map((record) => [record.eventId, record]));
    const categoryById = new Map(categories.map((category) => [category.id, category]));
    const featureById = new Map(features.map((feature) => [feature.id, feature]));
    const roomById = new Map(rooms.map((room) => [room.id, room]));
    const mileageById = new Map(mileageEntries.map((entry) => [entry.id, entry]));
    const productNames = await this.productNames();
    // Money is often linked to an estimate LINE rather than to the feature
    // directly; the accountant still wants to know which feature and room it
    // belongs to, so resolve through the line (found in acceptance).
    const lineFeature = new Map(
      (
        await this.db.select<{ id: string; feature_id: string | null }>(
          "SELECT id, feature_id FROM proposed_cost_lines WHERE project_id = ?",
          [projectId],
        )
      ).map((row) => [row.id, row.feature_id]),
    );

    // Allocation → purchase line → product, for the product column.
    const productByAllocation = new Map<string, string>();
    for (const graph of graphs) {
      for (const allocation of graph.allocations) {
        const line = graph.lines.find((candidate) => candidate.id === allocation.lineId);
        if (line?.productId) productByAllocation.set(allocation.id, productNames.get(line.productId) ?? "");
      }
    }

    const documentedExpenses = new Set<string>();
    for (const record of provenance) {
      if (record.expenseId && record.attachments.length > 0) documentedExpenses.add(record.expenseId);
    }

    return live.map((event) => {
      const record = byEvent.get(event.id);
      const category = categoryById.get(event.categoryId);
      const bucket = (event.bucketOverride ?? category?.defaultBucket ?? "other") as FinancialBucket;
      const featureId =
        event.linkedFeatureId ??
        (event.linkedProposedLineId ? (lineFeature.get(event.linkedProposedLineId) ?? null) : null);
      const feature = featureId ? featureById.get(featureId) : undefined;
      const room = feature?.roomId ? roomById.get(feature.roomId) : undefined;
      const mileageEntry = event.source === "mileage" ? mileageById.get(event.id) : undefined;
      const attachments = record?.attachments ?? [];
      const documentStatus: DocumentStatus =
        event.source === "mileage"
          ? "not-required"
          : attachments.length > 0
            ? "attached"
            : record?.expenseId && documentedExpenses.has(record.expenseId)
              ? "attached"
              : "missing";
      return {
        eventId: event.id,
        date: event.date,
        vendor: record?.vendor ?? "",
        description: event.description,
        amountCents: event.amountCents,
        taxCents: event.taxCents,
        totalCents: event.amountCents + event.taxCents,
        bucket,
        bucketLabel: bucketLabel(bucket),
        categoryName: category?.displayName ?? "Uncategorized",
        costBasis: event.costType,
        sourceKind:
          event.source === "mileage" ? "mileage" : record?.isReturn ? "return" : event.source === "expense" ? "expense" : "purchase",
        sourceLabel: record?.sourceLabel ?? (event.source === "mileage" ? "Mileage" : "Recorded"),
        documentStatus,
        documentName: attachments[0]?.originalFilename ?? null,
        receiptNumber: record?.receiptNumber ?? null,
        featureName: feature?.name ?? "",
        roomName: room?.name ?? "",
        productName: productByAllocation.get(event.id) ?? "",
        notes: mileageEntry ? `${mileageEntry.milesX10 / 10} miles at ${mileageEntry.rateCentsPerMile}c/mile` : "",
        purchaseId: record?.purchaseId ?? null,
        expenseId: record?.expenseId ?? null,
        isReturn: record?.isReturn ?? false,
      };
    });
  }


  /* ------------------------------------------------------------------ */
  /* Saved reports (spec §42I)                                           */
  /* ------------------------------------------------------------------ */

  /**
   * Keep a generated file with the project. Nothing is stored automatically:
   * a preview leaves no trace, and only an explicit "Save report to project"
   * writes anything. The file enters the ORDINARY managed attachment system,
   * so it is SHA-256 deduplicated, reference-counted and removed with the
   * project by the ownership manifest.
   */
  async saveGeneratedReport(
    projectId: string,
    input: { reportId: string; label: string; filename: string; bytes: Uint8Array },
  ): Promise<SavedReport> {
    const project = await this.projects.getProject(projectId);
    if (!project) throw new ValidationError(["That project no longer exists."]);
    const { attachment } = await this.attachments.importBytes(input.bytes, input.filename, "other");
    const now = new Date().toISOString();
    await this.db.execute(
      `INSERT OR REPLACE INTO project_attachments (project_id, attachment_id, report_id, label, created_at)
       VALUES (?, ?, ?, ?, ?)`,
      [projectId, attachment.id, input.reportId, input.label, now],
    );
    return {
      attachmentId: attachment.id,
      reportId: input.reportId,
      label: input.label,
      filename: attachment.originalFilename,
      sizeBytes: attachment.sizeBytes,
      mimeType: attachment.mimeType,
      createdAt: now,
      storedRelativePath: attachment.storedRelativePath,
    };
  }

  async listSavedReports(projectId: string): Promise<SavedReport[]> {
    const rows = await this.db.select<{
      attachment_id: string;
      report_id: string;
      label: string;
      created_at: string;
      original_filename: string;
      size_bytes: number;
      mime_type: string;
      stored_relative_path: string;
    }>(
      `SELECT l.attachment_id, l.report_id, l.label, l.created_at,
              a.original_filename, a.size_bytes, a.mime_type, a.stored_relative_path
         FROM project_attachments l
         JOIN attachments a ON a.id = l.attachment_id
        WHERE l.project_id = ? AND a.archived_at IS NULL
        ORDER BY l.created_at DESC`,
      [projectId],
    );
    return rows.map((row) => ({
      attachmentId: row.attachment_id,
      reportId: row.report_id,
      label: row.label,
      filename: row.original_filename,
      sizeBytes: Number(row.size_bytes),
      mimeType: row.mime_type,
      createdAt: row.created_at,
      storedRelativePath: row.stored_relative_path,
    }));
  }

  /** Unlink a saved report. The file survives if anything else references it. */
  async removeSavedReport(projectId: string, attachmentId: string): Promise<void> {
    await this.attachments.unlink(attachmentId, { type: "project", id: projectId });
  }

  private async productNames(): Promise<Map<string, string>> {
    const rows = await this.db.select<{ id: string; name: string }>("SELECT id, name FROM products", []);
    return new Map(rows.map((row) => [row.id, row.name]));
  }
}

function comparisonRows(rowsA: DesignScopeRow[], rowsB: DesignScopeRow[]) {
  const keys = new Set([
    ...rowsA.map((row) => `${row.roomName}|${row.featureName}`),
    ...rowsB.map((row) => `${row.roomName}|${row.featureName}`),
  ]);
  const totalOf = (rows: DesignScopeRow[], key: string) =>
    rows.filter((row) => `${row.roomName}|${row.featureName}` === key).reduce((sum, row) => sum + row.amountCents, 0);
  return [...keys].sort().map((key) => {
    const [roomName, featureName] = key.split("|");
    const a = totalOf(rowsA, key);
    const b = totalOf(rowsB, key);
    return [
      cell.text(roomName || "Project-wide"),
      cell.text(featureName),
      cell.money(a),
      cell.money(b),
      cell.money(b - a),
    ];
  });
}

/**
 * The projection labels a room with its full path ("Main Floor → Kitchen");
 * a frozen baseline line stores only the room name. Compare the leaf.
 */
function roomKey(label: string): string {
  const leaf = label.split("→").pop()?.trim() ?? "";
  return leaf || "Project";
}

function bucketLabel(bucket: string): string {
  return bucket.charAt(0).toUpperCase() + bucket.slice(1);
}

function procurementStatusLabel(status: string): string {
  switch (status) {
    case "fulfilled":
      return "Purchased";
    case "partial":
      return "Partially purchased";
    case "over":
      return "Over-purchased";
    case "none":
      return "Not purchased";
    default:
      return status.charAt(0).toUpperCase() + status.slice(1);
  }
}

/** Job-numbered, dated, filesystem-safe: JOB26016-accounting-2026-08-21. */
export function exportName(identity: ReportIdentity, reportSlug: string): string {
  const date = new Date().toISOString().slice(0, 10);
  return `JOB${identity.jobNumber}-${reportSlug}-${date}`.replace(/[^A-Za-z0-9._-]/g, "-");
}

export { documentLabel };
